import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ChartDataTable } from './ChartDataTable';
import { useZoomDetection } from '../hooks/useZoomDetection';
import { useScreenReaderDetection } from '../hooks/useScreenReaderDetection';
import { exportCSV } from '../utils/csvExport';
import { generateCSVData } from '../utils/dataTransformers';
import {
  createTopSearchesChartOptions,
  createAgreementsChartOptions,
  createAccountActivityChartOptions,
  createSimpleHorizontalChartOptions,
} from '../utils/chartOptions';
import { STATISTICS_TAB_CONFIG } from '../config/tabConfig';
import { ViewModeToggle } from './ViewModeToggle';

interface StatisticsChartProps {
  data: any;
  tabKey: string;
  isMobile?: boolean;
}

const calculateLabelLines = (label: string, maxLength: number = 40): number => {
  if (!label) return 1;

  // Get actual zoom level
  const baseRatio = parseFloat(localStorage.getItem('basePixelRatio') || '1');
  const currentRatio = window.devicePixelRatio || 1;
  const zoomLevel = currentRatio / baseRatio;

  // Adjust max length based on zoom
  let adjustedMaxLength: number;
  if (zoomLevel >= 3.5)
    adjustedMaxLength = Math.max(12, Math.floor(maxLength / 3.5));
  else if (zoomLevel >= 2.5)
    adjustedMaxLength = Math.max(18, Math.floor(maxLength / 2.5));
  else if (zoomLevel >= 1.5)
    adjustedMaxLength = Math.max(22, Math.floor(maxLength / 1.8));
  else adjustedMaxLength = maxLength;

  if (label.length <= adjustedMaxLength) return 1;

  // Count words to determine if we need extra lines
  const wordCount = label.split(' ').length;
  const estimatedLines = Math.ceil(label.length / adjustedMaxLength);

  // Be more generous with line allowance for agreements
  let maxLines = 4; // Start with 4 lines minimum
  if (wordCount >= 8)
    maxLines = 7; // Very long labels (8-10 words)
  else if (wordCount >= 6)
    maxLines = 6; // Long labels (6-7 words)
  else if (wordCount >= 4) maxLines = 5; // Medium labels (4-5 words)

  // Add extra lines at high zoom
  if (zoomLevel >= 3) maxLines += 2;
  else if (zoomLevel >= 2) maxLines += 1;

  // Always return at least the estimated lines needed
  return Math.max(estimatedLines, Math.min(estimatedLines + 1, maxLines));
};

const calculateTotalLines = (labels: string[]): number =>
  labels.reduce((total, label) => total + calculateLabelLines(label), 0);

const getChartHeight = (
  tabKey: string,
  labels: string[],
  isMobile: boolean
) => {
  const totalLines = calculateTotalLines(labels);
  const numLabels = labels.length;

  // Get base pixel ratio to detect actual zoom vs high DPI
  const baseRatio = parseFloat(localStorage.getItem('basePixelRatio') || '1');
  const currentRatio = window.devicePixelRatio || 1;
  const zoomLevel = currentRatio / baseRatio;

  // Dynamic configuration based on content
  const configs = {
    accountActivity: {
      baseHeight: 40, // Height per line of text
      labelSpacing: 15, // Extra space between labels
      padding: 150, // Top + bottom padding
      minHeight: 300, // Minimum height
    },
    agreements: {
      baseHeight: 25, // Adjusted height per line
      labelSpacing: 6, // Minimal spacing between labels
      padding: 150, // Reduced padding to match other charts
      minHeight: 250, // Lower minimum height
    },
    topSearches: {
      baseHeight: 30, // Slightly more height to account for 1.3 line height
      labelSpacing: 6, // Same minimal spacing as agreements
      padding: 150, // Same padding as agreements
      minHeight: 250, // Same minimum as agreements
    },
  };

  const config = configs[tabKey as keyof typeof configs];
  if (!config) return isMobile ? 300 : 400;

  // Dynamic height calculation
  let baseHeightMultiplier = config.baseHeight;
  let spacingMultiplier = config.labelSpacing;

  // Adjust for zoom level
  if (zoomLevel >= 3) {
    baseHeightMultiplier *= 1.6; // 60% more space at very high zoom
    spacingMultiplier *= 1.5;
  } else if (zoomLevel >= 2) {
    baseHeightMultiplier *= 1.4; // 40% more space at high zoom
    spacingMultiplier *= 1.3;
  } else if (zoomLevel >= 1.5) {
    baseHeightMultiplier *= 1.2; // 20% more space at medium zoom
    spacingMultiplier *= 1.15;
  }

  // Calculate dynamic height based on actual content
  // Account for both line wrapping and spacing between labels
  const heightForLines = totalLines * baseHeightMultiplier;
  const heightForSpacing = numLabels * spacingMultiplier;
  const calculatedHeight = heightForLines + heightForSpacing + config.padding;

  // Only apply minimum height, no maximum
  return Math.max(config.minHeight, calculatedHeight);
};

const getChartOptions = (tabKey: string, theme: any) => {
  const optionsMap: Record<string, any> = {
    topSearches: createTopSearchesChartOptions(theme),
    agreements: createAgreementsChartOptions(theme),
    accountActivity: createAccountActivityChartOptions(theme),
    noResults: createSimpleHorizontalChartOptions(theme),
    clicks: createSimpleHorizontalChartOptions(theme),
    searchFilters: createSimpleHorizontalChartOptions(theme),
  };
  // Default to simple horizontal chart options for any unknown chart types
  return optionsMap[tabKey] || createSimpleHorizontalChartOptions(theme);
};

export const StatisticsChart: React.FC<StatisticsChartProps> = ({
  data,
  tabKey,
  isMobile = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartRef = useRef<any>(null);
  const { isVeryCompact } = useZoomDetection();
  const isScreenReader = useScreenReaderDetection();
  const [viewMode, setViewMode] = useState<'chart' | 'table'>(
    isVeryCompact || isScreenReader ? 'table' : 'chart'
  );
  const [announcement, setAnnouncement] = useState('');
  const [forceRedraw, setForceRedraw] = useState(0);

  // Track zoom level changes and force redraw
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let lastPixelRatio = window.devicePixelRatio;

    const handleResize = () => {
      // Clear existing timeout
      if (timeoutId) clearTimeout(timeoutId);

      // Debounce to avoid too many redraws
      timeoutId = setTimeout(() => {
        // Check if pixel ratio actually changed (zoom change)
        if (window.devicePixelRatio !== lastPixelRatio) {
          lastPixelRatio = window.devicePixelRatio;
          // Force complete re-render of the chart
          setForceRedraw((prev) => prev + 1);
        } else {
          // Just a resize, still trigger redraw
          setForceRedraw((prev) => prev + 1);
        }
      }, 200);
    };

    // Listen for zoom changes via resize events
    window.addEventListener('resize', handleResize);

    // Poll for devicePixelRatio changes (more reliable for zoom detection)
    const pollInterval = setInterval(() => {
      if (window.devicePixelRatio !== lastPixelRatio) {
        lastPixelRatio = window.devicePixelRatio;
        handleResize();
      }
    }, 250); // More responsive polling

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(pollInterval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const height = useMemo(() => {
    if (!data?.chart) return isMobile ? 300 : 400;
    return getChartHeight(tabKey, data.chart.labels || [], isMobile);
  }, [data, tabKey, isMobile, forceRedraw]); // Added forceRedraw dependency

  // Recalculate options when zoom changes
  const options = useMemo(() => {
    return getChartOptions(tabKey, theme);
  }, [tabKey, theme, forceRedraw]); // Recalculate on zoom change

  useEffect(() => {
    setViewMode(isVeryCompact || isScreenReader ? 'table' : 'chart');
  }, [isVeryCompact, isScreenReader]);

  if (!data?.chart) return null;

  const getAccessibleDescription = () => {
    if (!data?.chart) return '';
    const { labels, datasets } = data.chart;
    return labels
      .map((label: string, index: number) => {
        const values = datasets
          .map(
            (dataset: any) =>
              `${dataset.label}: ${dataset.data[index]?.toLocaleString() || 0}`
          )
          .join(', ');
        return `${label}: ${values}`;
      })
      .join('. ');
  };

  const handleViewModeChange = (newMode: 'chart' | 'table') => {
    setViewMode(newMode);
    const modeText =
      newMode === 'table'
        ? t('statistics.a11y.switchedToTable', 'Switched to table view')
        : t('statistics.a11y.switchedToChart', 'Switched to chart view');
    setAnnouncement(String(modeText));
    setTimeout(() => setAnnouncement(''), 100);
  };

  const handleDownload = () => {
    const csvData = generateCSVData(tabKey, data, t);
    if (csvData.length > 0) {
      const tabConfig = STATISTICS_TAB_CONFIG.find((tab) => tab.key === tabKey);
      const title = tabConfig ? t(tabConfig.labelKey) : tabKey;
      exportCSV(csvData, String(title));
    }
  };

  // Get description from tab config
  const tabConfig = STATISTICS_TAB_CONFIG.find((tab) => tab.key === tabKey);
  const description = tabConfig ? t(tabConfig.helpKey) : '';

  // Voiceover/alt-text: use tab-specific alt text when available, else generic aria label
  const chartAltTextKey = `statistics.chart.altText.${tabKey}`;
  const chartAriaLabel =
    t(chartAltTextKey) !== chartAltTextKey
      ? String(t(chartAltTextKey))
      : String(
          t('statistics.chart.ariaLabel', {
            type: tabKey,
            count: data.chart.labels?.length || 0,
          })
        );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={8}
        my={12}
        sx={{
          flexWrap: { sm: 'wrap' },
        }}
      >
        {/* Description */}
        {description && (
          <Typography
            variant="body2"
            sx={{
              fontStyle: 'italic',
              flex: { sm: '1 1 320px' },
              minWidth: 0,
            }}
          >
            {description}
          </Typography>
        )}

        {/* View mode toggle and download */}
        <ViewModeToggle
          value={viewMode}
          onChange={handleViewModeChange}
          onDownload={handleDownload}
          size="small"
          sx={{
            flexWrap: 'nowrap',
            flexShrink: 0,
            alignSelf: { sm: 'flex-start' },
          }}
        />
      </Stack>

      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {viewMode === 'table' ? (
        <ChartDataTable data={data.chart} tabKey={tabKey} />
      ) : (
        <Box
          sx={{
            height,
            position: 'relative',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden', // Prevent horizontal scroll
            '& > div': {
              position: 'relative !important',
              height: '100%',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
            },
            '& canvas': {
              maxWidth: '100% !important', // Ensure canvas doesn't exceed container
              width: '100% !important',
              height: 'auto !important',
            },
          }}
        >
          <div role="img" aria-label={chartAriaLabel}>
            <Bar
              key={`chart-${tabKey}-${forceRedraw}`} // Force redraw on zoom change
              ref={chartRef}
              data={data.chart}
              options={options}
              aria-hidden="true"
            />
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              <p>{t('statistics.chart.description', { type: tabKey })}.</p>
              <p>{getAccessibleDescription()}</p>
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};
